<template>
  <div v-if="entitlementBlocked" class="settings-page" />
  <div v-else class="settings-page">
    <div class="automations-page-header">
      <div>
        <h2>Automations</h2>
        <div class="subtitle">All automations across your agents.</div>
      </div>
      <BaseButton
        kind="main"
        type="primary"
        :text="createAutomationButtonState === 'loading' ? 'Creating…' : 'New'"
        :state="createAutomationButtonState"
        @click="onCreateAutomation"
      >
        <template #icon>
          <AddIcon />
        </template>
      </BaseButton>
    </div>

    <AutomationsList
      :automations="allAutomations"
      :organization-id="orgId"
      :deleting-automation-id="deletingAutomationId"
      :disabling-automation-id="disablingAutomationId"
      :running="running"
      :show-agent-column="true"
      @navigate="navigateToAutomation"
      @navigate-agent="navigateToAgent"
      @rename="onRenameAutomation"
      @run="onRunAutomation"
      @disable="onDisableAutomation"
      @enable="onEnableAutomation"
      @delete="onDeleteAutomation"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, h, ref, toRef, watch } from "vue";
import { useModal } from "vue-final-modal";

import type { AnyAgentId, AutomationId } from "@ctrl/controller-rpc";

import { isAssistant } from "@/components/ai/agents";
import type { AutomationsListItem } from "@/components/ai/AutomationsList.vue";
import AutomationsList from "@/components/ai/AutomationsList.vue";
import NewAutomationModalBody from "@/components/ai/NewAutomationModalBody.vue";
import { ORPHAN_AGENT_KEY } from "@/components/ai/useAutomations";
import BaseButton from "@/components/base/BaseButton.vue";
import type { SelectOption } from "@/components/base/BaseInputSelect.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import AddIcon from "@/components/icons/AddIcon.vue";
import * as inject from "@/components/inject";
import Modal from "@/components/modals/Modal.vue";
import { openUpgradeModal } from "@/components/modals/upgrade";
import templates, { instantiate } from "@/components/templates";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";

export default defineComponent({
  name: "SettingsAutomations",
  components: { AddIcon, AutomationsList, BaseButton },
  props: {
    orgId: {
      type: String,
      required: true,
    },
  },
  async setup(props) {
    const orgId = toRef(props, "orgId");
    const billing = inject.billing();
    // Account-level: any org's plan grants automations.
    const isEntitledToAutomations = computed(
      () => billing.value?.isEntitledTo("ai-automations")?.value ?? false
    );
    const entitlementBlocked = computed(() => !isEntitledToAutomations.value);

    watch(
      () => billing.value?.isEntitledTo("ai-automations")?.value,
      (entitled) => {
        if (entitled === false) {
          openUpgradeModal(billing, undefined, "ai-automations");
        }
      },
      // Fire on enter: billing is usually already loaded when navigating here
      // from the Assistant, so a change-only watch never opened the modal and
      // left the locked blank page.
      { immediate: true }
    );

    const router = inject.router();
    const controller = inject.controller();
    const automations = inject.automations();
    const agents = inject.agents();

    const activeAgents = computed(
      () => agents.value?.active?.[orgId.value] ?? []
    );
    // The personal assistant is not part of `agents.active`; offer it in its
    // own organization (the personal one) so automations created from the
    // assistant get attached to it instead of ending up orphaned.
    const assistantOption = computed<SelectOption | null>(() => {
      const assistant = automations.value?.assistant.value;
      if (!assistant || assistant.organizationId !== orgId.value) return null;
      return {
        value: assistant.agentId,
        label: "Assistant",
        aliases: [assistant.agentId, "Assistant"],
      } satisfies SelectOption;
    });
    const agentOptions = computed<SelectOption[]>(() => [
      ...(assistantOption.value ? [assistantOption.value] : []),
      ...activeAgents.value.map((agent) => {
        const option = {
          value: agent.id,
          label: agent.name ?? "Unnamed agent",
          aliases: [agent.id, agent.name ?? ""].filter(Boolean),
        } satisfies SelectOption;
        return option;
      }),
    ]);
    const selectedAgentId = ref<AnyAgentId | null>(null);

    watch(
      agentOptions,
      (options) => {
        if (!selectedAgentId.value) return;
        if (options.some((option) => option.value === selectedAgentId.value)) {
          return;
        }
        selectedAgentId.value = null;
      },
      { immediate: true }
    );

    const getCatalogAvatarForAgent = (agentId: string): string | undefined => {
      if (!agents.value) return undefined;
      for (const key of Object.keys(agents.value.catalog)) {
        const list = agents.value.listActive(orgId.value, key);
        if (list.some((a) => a.id === agentId)) {
          return agents.value.catalog[key]?.avatar || undefined;
        }
      }
      return undefined;
    };

    const allAutomations = computed<AutomationsListItem[]>(() => {
      const byOrg = automations.value?.all.value?.[orgId.value];
      if (!byOrg) return [];

      return Object.entries(byOrg)
        .flatMap(([agentId, byAgent]) => {
          const isOrphan = agentId === ORPHAN_AGENT_KEY;
          const activeAgent = isOrphan
            ? undefined
            : activeAgents.value.find((a) => a.id === agentId);
          const agentName = isOrphan
            ? undefined
            : isAssistant(agentId)
              ? "Assistant"
              : (activeAgent?.name ?? "Unknown agent");
          const agentAvatar = isOrphan
            ? undefined
            : getCatalogAvatarForAgent(agentId);

          return Object.values(byAgent).map((automation) => ({
            id: automation.id,
            name: automation.name,
            agentId: isOrphan ? undefined : agentId,
            agentName,
            agentAvatar,
            trigger: automation.trigger,
            stepCount: automation.stepCount,
            enabled: automation.enabled,
          }));
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    });

    const navigateToAutomation = (automationId: AutomationId) => {
      navigateTo(RouteNames.SettingsAutomation, router, {
        params: { orgId: orgId.value, automationId },
      });
    };

    const navigateToAgent = (agentId: string) => {
      navigateTo(RouteNames.Agent, router, { params: { agentId } });
    };

    const deletingAutomationId = ref<AutomationId | null>(null);

    const onDeleteAutomation = async (automationId: AutomationId) => {
      if (deletingAutomationId.value) return;
      deletingAutomationId.value = automationId;
      try {
        await controller.ai.automation.delete(orgId.value, automationId);
      } finally {
        deletingAutomationId.value = null;
      }
    };

    const disablingAutomationId = ref<AutomationId | null>(null);

    const onDisableAutomation = async (automationId: AutomationId) => {
      if (disablingAutomationId.value) return;
      disablingAutomationId.value = automationId;
      try {
        await controller.ai.automation.update(orgId.value, automationId, {
          enabled: false,
        });
      } finally {
        disablingAutomationId.value = null;
      }
    };

    const onEnableAutomation = async (automationId: AutomationId) => {
      if (disablingAutomationId.value) return;
      disablingAutomationId.value = automationId;
      try {
        await controller.ai.automation.update(orgId.value, automationId, {
          enabled: true,
        });
      } finally {
        disablingAutomationId.value = null;
      }
    };

    const onRunAutomation = (automationId: AutomationId) => {
      const byOrg = automations.value?.all.value?.[orgId.value];
      if (!byOrg) return;

      for (const [agentId, byAgent] of Object.entries(byOrg)) {
        if (byAgent[automationId]) {
          void automations.value?.run(
            orgId.value,
            agentId === ORPHAN_AGENT_KEY ? null : agentId,
            automationId,
            undefined
          );
          return;
        }
      }
    };

    const running = computed(() => automations.value?.running.value ?? []);

    // ── Rename ────────────────────────────────────────────────────────────────

    const renamingAutomationId = ref<AutomationId | null>(null);
    const renameValue = ref("");
    const renameButtonState = computed(() =>
      renameValue.value.trim() ? "default" : "disabled"
    );

    const submitRename = async (): Promise<boolean> => {
      const name = renameValue.value.trim();
      const automationId = renamingAutomationId.value;
      if (!name || !automationId) return false;
      try {
        await controller.ai.automation.update(orgId.value, automationId, {
          name,
        });
        return true;
      } catch {
        return false;
      }
    };

    const { open: openRenameModal, close: closeRenameModal } = useModal({
      component: Modal,
      attrs: {
        title: "Rename automation",
        showTitleBar: true,
        showTopBar: false,
        modalButtons: [
          {
            title: "Rename",
            kind: "main",
            state: renameButtonState,
            func: submitRename,
          },
        ],
      },
      slots: {
        default: () =>
          h("div", { style: { display: "flex", flexDirection: "column" } }, [
            h("p", { style: { margin: "0 0 12px" } }, "Automation name"),
            h(FormInput, {
              value: renameValue.value,
              type: "text",
              placeholder: "Automation name",
              state: "default",
              "onUpdate:value": (v: string) => {
                renameValue.value = v;
              },
              onKeydown: async (e: KeyboardEvent) => {
                if (e.key === "Enter" && renameValue.value.trim()) {
                  const ok = await submitRename();
                  if (ok) closeRenameModal();
                }
              },
            }),
          ]),
      },
    });

    const onRenameAutomation = (
      automationId: AutomationId,
      currentName: string
    ) => {
      renamingAutomationId.value = automationId;
      renameValue.value = currentName;
      openRenameModal();
    };

    // ── Create ────────────────────────────────────────────────────────────────

    const isCreating = ref(false);
    const newAutomationName = ref("");
    const createAutomationButtonState = computed(() => {
      if (isCreating.value) return "loading";
      return "default";
    });
    const createModalButtonState = computed(() => {
      if (isCreating.value) return "loading";
      if (!newAutomationName.value.trim()) return "disabled";
      return "default";
    });

    const createAutomation = async (): Promise<boolean> => {
      const name = newAutomationName.value.trim();
      const agentId = selectedAgentId.value;
      if (!name || isCreating.value) return false;
      isCreating.value = true;
      try {
        const firstStepId = await controller.ai.step.create(
          orgId.value,
          {
            name: "Step 1",
            prompt: {
              context: {},
              document: { blocks: instantiate(templates.empty) },
            },
          },
          null
        );
        const automationId = await controller.ai.automation.create(
          orgId.value,
          name,
          [firstStepId],
          agentId
        );
        await navigateTo(RouteNames.SettingsAutomation, router, {
          params: { orgId: orgId.value, automationId },
        });
        return true;
      } catch {
        return false;
      } finally {
        isCreating.value = false;
      }
    };

    const { open: openCreateModal, close: closeCreateModal } = useModal({
      component: Modal,
      attrs: {
        title: "New automation",
        showTitleBar: true,
        showTopBar: false,
        modalButtons: [
          {
            title: "Create",
            kind: "main",
            state: createModalButtonState,
            func: createAutomation,
          },
        ],
      },
      slots: {
        default: () =>
          h(NewAutomationModalBody, {
            modelValue: newAutomationName.value,
            "onUpdate:modelValue": (value: string) => {
              newAutomationName.value = value;
            },
            agentValue: selectedAgentId.value ?? "",
            agentOptions: agentOptions.value,
            showAgentSelector: true,
            "onUpdate:agentValue": (value: string) => {
              selectedAgentId.value = value || null;
            },
            onSubmit: async () => {
              if (newAutomationName.value.trim()) {
                const ok = await createAutomation();
                if (ok) closeCreateModal();
              }
            },
          }),
      },
    });

    const onCreateAutomation = () => {
      newAutomationName.value = "";
      // In the personal organization, attach new automations to the
      // assistant by default rather than creating them agentless.
      selectedAgentId.value = assistantOption.value?.value ?? null;
      openCreateModal();
    };

    return {
      entitlementBlocked,
      allAutomations,
      deletingAutomationId,
      disablingAutomationId,
      navigateToAutomation,
      navigateToAgent,
      onDeleteAutomation,
      onDisableAutomation,
      onEnableAutomation,
      onRunAutomation,
      onRenameAutomation,
      onCreateAutomation,
      createAutomationButtonState,
      running,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../../styles/settings.scss";

.automations-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;

  h2 {
    margin-bottom: 4px;
  }

  .subtitle {
    margin: 0;
  }
}
</style>
